// Anchors a singleton on globalThis so identity survives both HMR re-evaluation
// AND Vite serving the same module under multiple module IDs (e.g. mixed @/ alias
// vs ./relative imports, optimizeDeps re-bundles, Windows path normalization).
//
// Every cross-HMR singleton in this app — zustand stores, dialog registries,
// React refs/contexts, the tRPC client, the QueryClient — must use this helper
// instead of `import.meta.hot.data`.
//
// Production semantics are identical to the previous `import.meta.hot?.data[k] ?? factory()`
// pattern: modules evaluate once, factories run once, one instance per key.
// In dev, the registry can be inspected via `globalThis.__triffinSingletons`.
//
// IMPORTANT — TDZ-hardening: this module deliberately has ZERO module-scope value
// bindings (no `const`, no `let`, no `var`). Function declarations are hoisted and
// are the only things at module scope. Reason: under HMR re-evaluation, if singleton.ts
// is being re-evaluated and an importer's module body synchronously calls
// `getOrCreateSingleton` before the corresponding `const` line had executed, the call
// would throw "Cannot access 'X' before initialization". By having no module-scope
// bindings at all, this hazard cannot exist.
//
// The `__triffinSingletons` global key is intentionally inlined (not extracted to a
// const) for the same TDZ-safety reason.

type Registry = Map<string, unknown>;

function getRegistry(): Registry {
	const g = globalThis as unknown as { __triffinSingletons?: Registry };
	let registry = g.__triffinSingletons;
	if (!registry) {
		registry = new Map<string, unknown>();
		g.__triffinSingletons = registry;
	}
	return registry;
}

export function getOrCreateSingleton<T>(key: string, factory: () => T): T {
	const registry = getRegistry();
	if (registry.has(key)) return registry.get(key) as T;
	const created = factory();
	registry.set(key, created);
	return created;
}
